import { useEffect } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';

/**
 * `useBootstrap()` (W127-F2) — Phase 21.
 *
 * Mounts ONE TanStack query against `/api/v1/auth/me/bootstrap` (the W127-F1
 * endpoint) at the app-shell level via `<BootstrapProvider>`. The response
 * carries memberships + subtree + notifications-summary in a single trip.
 *
 * The provider then **seeds the existing query caches** so legacy hooks
 * (`useMembershipsQuery`, `useOrgSubtree('me')`, `useNotificationsUnread`)
 * continue to work unchanged — they just hit hot data instantly because
 * the bootstrap query pre-populated their cache keys.
 *
 * Why seed instead of refactor every call-site: the legacy hooks are
 * referenced in 30+ files. Refactoring all of them to read from
 * BootstrapContext would be wave-scale churn. Seeding lets them stay
 * but eliminates the per-mount network round-trip that the post-Phase-20
 * audit caught — 250 × 429 across 26/33 routes because each mount
 * independently fired the same endpoint.
 *
 * staleTime: 5min. The bootstrap is a session-start optimization; data
 * older than 5min refetches on next mount.
 */

interface BootstrapSubtreeRow {
  id: string;
  parent_id: string | null;
  tier: 'platform' | 'partner' | 'customer';
  name: string;
  slug?: string;
  children_count?: number;
  buildings_count?: number;
}

export interface BootstrapResponse {
  memberships: {
    homeOrgId: string | undefined;
    memberships: Array<{
      entity_type: 'building' | 'building_operational' | 'partner_relationship';
      entity_id: string;
      organization_id: string;
      role: string;
      source:
        | 'user_building_access'
        | 'building_operational_roles'
        | 'partner_relationships';
      effective_from: string;
      effective_until: string | null;
    }>;
  };
  subtree: BootstrapSubtreeRow[];
  notificationsSummary: {
    unreadCount: number;
    latestAt: string | null;
  };
  warnings: Array<{ block: 'subtree' | 'notifications'; message: string }>;
}

const BOOTSTRAP_KEY = ['auth', 'me', 'bootstrap'] as const;

export function useBootstrapQuery(): UseQueryResult<BootstrapResponse> {
  const { isAuthenticated, getAccessToken } = useAuth();
  return useQuery({
    queryKey: BOOTSTRAP_KEY,
    queryFn: async () =>
      apiFetch<BootstrapResponse>('/api/v1/auth/me/bootstrap', {
        getAccessToken,
      }),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Side-effect: when bootstrap resolves, seed the legacy query caches so
 * consumers reading `['memberships', 'me']` / `['org-subtree', 'me']` /
 * `['notifications', null, null, null]` (the unread-list cache key) hit
 * hot data without a refetch.
 *
 * Keep in sync with:
 *   - `apps/web/src/lib/api/memberships.ts` MEMBERSHIPS_KEY
 *   - `apps/web/src/features/orgs/api/use-org-subtree.ts` queryKey
 *   - `apps/web/src/features/notifications/api/use-notifications.ts` notificationsQueryKey
 */
export function useBootstrapSeed(): void {
  const queryClient = useQueryClient();
  const bootstrap = useBootstrapQuery();
  useEffect(() => {
    if (!bootstrap.data) return;
    queryClient.setQueryData(['memberships', 'me'], bootstrap.data.memberships);
    queryClient.setQueryData(['org-subtree', 'me'], bootstrap.data.subtree);
    // The notifications hook keys on `(unreadOnly, since, limit)` — the
    // sidebar badge reads via `useNotificationsUnread()` which calls
    // `useNotifications({ limit: 10 })` → key `[..., null, null, 10]`.
    // Seed it with a minimal NotificationPoll shape carrying just the
    // count; the bell dropdown will fetch the full list on open.
    queryClient.setQueryData(['notifications', null, null, 10], {
      items: [],
      total: bootstrap.data.notificationsSummary.unreadCount,
    });
  }, [bootstrap.data, queryClient]);
}
